import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "../utils";
import { logoMap } from "./logo-map";
import type { LogoBaseProps } from "./logo-base";
import type { LogoName } from "./types";

/**
 * Logo Component
 *
 * Design System: V&A Waterfront Foundation Design System
 *
 * @example
 * // Basic usage
 * <Logo name="va-waterfront" />
 *
 * // With size variant
 * <Logo name="va-waterfront" size="lg" />
 *
 * // Filled variant
 * <Logo name="va-waterfront" filled />
 *
 * // Non-decorative (for accessibility)
 * <Logo name="va-waterfront" decorative={false} aria-label="V&A Waterfront logo" />
 */

export interface LogoProps extends Omit<LogoBaseProps, "children"> {
  /**
   * The name of the logo to render
   */
  name: LogoName;
  /**
   * If true, renders the filled variant of the logo
   * @default false
   */
  filled?: boolean;
  /**
   * If true, renders the logo for dark backgrounds (light colored)
   * @default false
   */
  isDark?: boolean;
}
const Logo = React.forwardRef<SVGSVGElement, LogoProps>((t0, ref) => {
  const $ = _c(15);
  let className;
  let isDark;
  let name;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      name,
      filled: t1,
      isDark,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = isDark;
    $[3] = name;
    $[4] = props;
    $[5] = t1;
  } else {
    className = $[1];
    isDark = $[2];
    name = $[3];
    props = $[4];
    t1 = $[5];
  }
  const filled = t1 === undefined ? false : t1;
  const LogoComponent = logoMap[name];
  if (!LogoComponent) {
    return null;
  }
  const t2 = filled ? "filled" : "outlined";
  const t3 = isDark && "text-white";
  let t4;
  if ($[6] !== className || $[7] !== t3) {
    t4 = cn(t3, className);
    $[6] = className;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== LogoComponent || $[10] !== props || $[11] !== ref || $[12] !== t2 || $[13] !== t4) {
    t5 = <LogoComponent ref={ref} variant={t2} className={t4} {...props} />;
    $[9] = LogoComponent;
    $[10] = props;
    $[11] = ref;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
});
Logo.displayName = "Logo";
export { Logo };